<script>
import {useRoute, useRouter} from "vue-router";
import {usePossibleDuplicateStore} from "@/javascript/stores/possibleDuplicate";
import {useUserStore} from "@/javascript/stores/user";
import LoadingSpinner from "@/javascript/views/components/loadingSpinner.vue";
import Paginator from "@/javascript/views/components/Pagination.vue";

export default {
  name: "DuplicateDonorsList",
  components: {LoadingSpinner, Paginator},
  setup() {
    const route = useRoute();
    const router = useRouter();
    const possibleDuplicateStore = usePossibleDuplicateStore();
    const userStore = useUserStore();
    return {route, router, possibleDuplicateStore, userStore};
  },
  computed: {
    loading() {
      return this.possibleDuplicateStore.loadingReviews;
    },
    reviews() {
      return this.possibleDuplicateStore.reviews;
    }
  },
  mounted() {
    this.possibleDuplicateStore.loadReviews();
  },
  methods: {
    // Navigate to the review View for a primary donor (route-driven, mirrors Gifts).
    viewReview(primaryId) {
      const routeName = this.userStore.userScopedToSubdomain ? "client-dashboard-scoped" :
        "client-dashboard";
      this.router.push({
        name: routeName,
        params: {...this.route.params, panel: "duplicate-donors", id: primaryId}
      });
    },
    householdRoute(household) {
      const routeName = this.userStore.userScopedToSubdomain ? "client-dashboard-scoped" :
        "client-dashboard";
      const params = this.userStore.userScopedToSubdomain
        ? {panel: "households", id: household.id}
        : {org: this.userStore.currentOrganization?.slug, panel: "households", id: household.id};
      return {name: routeName, params, query: {returnToDuplicates: "true"}};
    }
  }
};
</script>

<template>
  <div class="duplicate-donors-list bg-white w-full pt-2">
    <!-- Header card on white, matching index pages -->
    <div class="p-4 px-6 mb-4">
      <p class="text-sm text-gray-600 max-w-3xl">
        These donors may have duplicate records. Review the details for each donor and use the merge
        tool to combine records when appropriate.
      </p>
    </div>

    <loading-spinner v-if="loading" />

    <div
      v-else
      class="overflow-hidden"
    >
      <table class="w-full text-xs clickable-rows">
        <thead>
          <tr>
            <th>Name</th>
            <th>Email</th>
            <th>Household</th>
            <th>Potential Matches</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="reviews.length === 0">
            <td
              colspan="4"
              class="text-center text-gray-500 py-6"
            >
              No possible duplicate donors to review.
            </td>
          </tr>
          <tr
            v-for="row in reviews"
            :key="row.primary_donor.id"
            @click="viewReview(row.primary_donor.id)"
          >
            <td>{{ row.primary_donor.full_name }}</td>
            <td>{{ row.primary_donor.email }}</td>
            <td>
              <router-link
                v-if="row.primary_donor.household"
                :to="householdRoute(row.primary_donor.household)"
                class="text-blue-600"
                @click.stop
              >
                {{ row.primary_donor.household.name }}
              </router-link>
              <span v-else>—</span>
            </td>
            <td>
              <span class="inline-block rounded-full bg-yellow-100 text-yellow-800 px-2 py-1">
                {{ row.potential_matches_count }} potential matches
              </span>
            </td>
          </tr>
        </tbody>
      </table>

      <paginator
        v-if="possibleDuplicateStore.meta"
        class="mt-4"
        :page="possibleDuplicateStore.meta.page"
        :total-pages="possibleDuplicateStore.meta.pages"
        :records-showing="reviews.length"
        :records-per-page="possibleDuplicateStore.meta.limit"
        :total-records="possibleDuplicateStore.meta.count"
        @next="possibleDuplicateStore.nextPage"
        @prev="possibleDuplicateStore.prevPage"
        @move-to-page="possibleDuplicateStore.moveToPage"
      />
    </div>
  </div>
</template>

<style scoped lang="scss">
@import '@/styles/_variables.scss';

.duplicate-donors-list {
  th {
    font-size: 10px;
    font-weight: normal;
    text-align: left;
    text-transform: none; /* not all-caps */
    @apply text-slate-500 px-2 py-3 border-b;
  }
  td {
    font-size: 12px;
    @apply px-2 py-4;
  }
  tbody tr {
    &:nth-child(odd) { @apply bg-white; }
    &:nth-child(even) { @apply bg-table_gray; }
  }
  .clickable-rows tbody tr:hover {
    cursor: pointer;
    td { @apply bg-gray-200; }
  }
}
</style>
